import type { ReelSectionWord } from './reelSectionWords';
import type { TranscriptElement } from '@src/types.ts/transcript';

/**
 * One passage of the source video a candidate contributes — a `ClipFragment`
 * read down to what a transcript slice actually needs.
 */
export type ClipSourceRange = {
  startTime: number;
  duration: number;
};

const isActive = (element: { state?: string }) =>
  element.state !== 'removed' && element.state !== 'cut';

/**
 * PLAT-11109 — the words a candidate clip says, on the clip's own clock.
 *
 * The Add Clips picker previews a clip that is not in the reel and has no
 * timeline of its own, so its scrub readout cannot go through
 * {@link reelSectionWords}: the words come from the **source story's**
 * transcript instead, taken from the ranges the clip was cut from and re-based
 * so the first word of the clip lands at ~0. That is the clock the row's player
 * and its tracker are both on, so {@link wordIndexAtTime}'s binary search works
 * against these unchanged.
 *
 * A candidate that is itself a reel arrives as several ranges, in playing
 * order. Each is placed after the one before it — the source time *between*
 * passages is not part of what plays, so it is not part of the clock either,
 * and the strip runs continuously across a join.
 *
 * Walked for the playing row only, once when it starts: a story transcript runs
 * to thousands of tokens, and drawing the list must not touch it at all.
 */
export function clipRangeWords(
  elements: readonly TranscriptElement[] | undefined,
  ranges: readonly ClipSourceRange[],
): ReelSectionWord[] {
  if (!elements?.length || ranges.length === 0) return [];

  const words: ReelSectionWord[] = [];
  // Where the range being read starts on the clip's clock: the lengths of every
  // passage before it.
  let clipOffset = 0;

  for (const range of ranges) {
    const rangeEnd = range.startTime + range.duration;
    // Whether the word last emitted came from this range's previous element,
    // which is what makes punctuation joinable — a comma carries no timing of
    // its own, so it can only be placed by the word in front of it.
    let isFollowingIncludedWord = false;

    for (const element of elements) {
      if (!isActive(element)) continue;
      const value = element.value?.trim() ?? '';
      if (!value) continue;

      if (element.type !== 'text') {
        // Punctuation joins the word it follows. On its own it would take a
        // turn under the highlight as though it had been spoken.
        if (isFollowingIncludedWord) {
          words[words.length - 1].value += value;
        }
        continue;
      }

      // Typed nullable at runtime — a text element whose timing never survived
      // an edit cannot be placed on the strip, so it is left out rather than
      // pinned to zero (`docs/transcript-ui-patterns.md`).
      const start = element.ts;
      if (typeof start !== 'number' || !Number.isFinite(start)) {
        isFollowingIncludedWord = false;
        continue;
      }
      if (start < range.startTime || start >= rangeEnd) {
        isFollowingIncludedWord = false;
        continue;
      }

      const end =
        typeof element.end_ts === 'number' && Number.isFinite(element.end_ts)
          ? element.end_ts
          : start;
      words.push({
        value,
        startTime: clipOffset + (start - range.startTime),
        // Held inside the passage: a word that runs past the cut is heard only
        // as far as the cut, and letting it overhang would leave the last word
        // of a passage highlighted into the first word of the next.
        endTime: clipOffset + Math.min(end, rangeEnd) - range.startTime,
      });
      isFollowingIncludedWord = true;
    }

    clipOffset += range.duration;
  }

  return words;
}
